// =============================================================================
// ui/tabs/SessionContractEditor.tsx — the pre-commitment, editable.
// -----------------------------------------------------------------------------
// Josh's screenshot settled this: the contract IS a live control on the
// Pre-Market tab, and the port had it read-only on the reasoning that it "carries
// a state machine". Half of that reasoning was right and it pointed the wrong way
// — the state machine is a reason to be careful about WHICH FIELDS the form
// writes, not a reason to withhold the form.
//
// ── 🚨 THE FORM OWNS FIVE FIELDS. IT MUST NOT WRITE THE OTHER FOUR ──
//
//   owned      accountIds · targetProfit · personalDLL · locked · lockedAt
//   NOT owned  dllHitAt · dllAcknowledgedAt · targetHitAt · targetAcknowledgedAt
//              (and manualPnl)
//
// The four are written DURING the session by the P&L watcher and are the record
// of whether the pledge held. `dllAcknowledgedAt` is string-compared against every
// later trade to flag it as an override of the trader's own daily stop, so
// overwriting one rewrites the history of a breach.
//
// `contractToBlob` merges over the row's stored blob and touches only the five.
// The form never sees the other four as editable state at all — it displays them.
//
// ── LOCK IS THE COMMIT. THERE IS NO SEPARATE SAVE ──
//
// One button, matching beta: "Lock Plan" when open, "Unlock" when locked. Locking
// validates (a target OR a DLL), stamps `lockedAt` and writes. Unlocking writes
// `locked: false` and NOTHING else — beta's comment is explicit that it preserves
// the recorded hit timestamps "so analytics history isn't destroyed", and
// `lockedAt` stays too.
//
// ⚠ That means an unlocked contract's typed numbers are working state until Lock,
// which is beta's model. A user who types a target and walks away has not pledged
// anything, and the button says so.
//
// ── 🔴 AND THAT IS WHY THIS FORM IS **NOT** AUTOSAVED (TCR-79) ──
//
// TCR-79 put commit-on-blur on every Journal surface after Josh lost a premarket
// plan to a phase switch — *"We shouldn't ever lose data."* The plan and the
// recap take it. **This form deliberately does not**, and the card's acceptance
// asking for it was wrong:
//
//   • LOCK IS THE COMMIT. There is no save to fire early. Autosaving the typed
//     numbers would persist a pledge the trader never made — the exact thing the
//     paragraph above says the button exists to prevent.
//   • `locked` is one of the five fields this form owns. Writing the numbers
//     without it stores a contract that is neither pledged nor absent, which is
//     a state neither client has ever read.
//
// ⚠ SO TYPED-BUT-UNLOCKED NUMBERS ARE STILL LOST ON A PHASE SWITCH, and that is
// the intended behaviour, not an oversight: nothing was pledged. If that is ever
// to change, it is a product decision about what a pledge means — not a bug, and
// not something to fix by adding a blur handler here.
// =============================================================================

import { useMemo, useState } from 'react';
import { JournalSection } from './JournalSection';
import { useAccounts } from '../state/accounts';
import { useSupabase } from '../state/supabase';
import { saveSessionContract } from '../../io/sync/saveSessionContract';
import { canLockContract } from '../../selectors/journal';
import type { SessionContract } from '../../selectors/journal';
import type { Account } from '../../selectors/accounts';
import { moneyTarget } from '../format';

export interface SessionContractEditorProps {
  contract: SessionContract;
  date: string;
  /** The row's stored blob, passed straight through to the writer. */
  rawContract: Readonly<Record<string, unknown>>;
  baselineUpdatedAt: string | null;
  onSaved: (
    contract: SessionContract, updatedAt: string | null,
    rawContract: Record<string, unknown>,
  ) => void;
}

export function SessionContractEditor(
  { contract, date, rawContract, baselineUpdatedAt, onSaved }: SessionContractEditorProps,
) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();

  // ⚠ The draft holds ONLY the three fields a user types or picks. `locked` and
  // `lockedAt` are decided by the button, and the other five are never draft
  // state at all — a field that is not in the draft cannot be written by mistake.
  const [accountIds, setAccountIds] = useState<string[]>(() => [...contract.accountIds]);
  const [target, setTarget] = useState(() => String(contract.targetProfit || ''));
  const [dll, setDll] = useState(() => String(contract.personalDLL || ''));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = contract.locked;
  const nTarget = Number(target) || 0;
  const nDll = Number(dll) || 0;
  const canLock = canLockContract({ targetProfit: nTarget, personalDLL: nDll });

  // ⚠ Keyed by `String(a.id)`. `Account.id` is a BRANDED type and
  // `SessionContract.accountIds` is plain `string[]` — the stored blob is JSON and
  // carries no brand. Matching them any other way needs a cast at every lookup.
  const byId = useMemo(
    () => new Map((accounts ?? []).map((a) => [String(a.id), a as Account])),
    [accounts],
  );

  const commit = async (nextLocked: boolean) => {
    setBusy(true);
    setError(null);
    // ⚠ Built by SPREADING the stored contract, so the four session-written
    // timestamps and `manualPnl` travel through untouched even before
    // `contractToBlob` merges again at the io layer. Two layers of the same
    // discipline, because this is the field set where being wrong is unrecoverable.
    const next: SessionContract = {
      ...contract,
      accountIds: [...accountIds],
      targetProfit: nTarget,
      personalDLL: nDll,
      locked: nextLocked,
      // ⚠ Stamped only when LOCKING, and never cleared. Unlocking keeps the
      // original — beta does the same, and "when did you first commit" is the
      // question this answers.
      lockedAt: nextLocked ? (contract.lockedAt ?? new Date().toISOString()) : contract.lockedAt,
    };

    const res = await saveSessionContract({
      sb, userId, date, contract: next, rawContract, baselineUpdatedAt,
    });
    setBusy(false);

    if (res.error) { setError(res.error); return; }
    // The blob just written becomes the one the NEXT save merges over.
    onSaved(next, res.updatedAt, { ...rawContract, ...contractOwned(next) });
  };

  const toggleAccount = (id: string) =>
    setAccountIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    /**
     * ⚠ 🚨 A REAL SECTION CARD, like everything else in this column. It used to
     * render a loose `<h4 class="jp-sec-title">` inside a plain div — the class
     * of a section card, on something that was not one — so it sat between five
     * cards with no border, no icon badge and no chevron. Found by opening the
     * page, not by a test: the heading was present and correct, which is all a
     * test can check.
     *
     * ⚠ THE LOCK STATE IS THE HEADER'S `meta`, so it reads where beta's does.
     * `⛨` is beta's glyph for the contract, and it is the same character the
     * glance tile above uses for the same concept.
     */
    <JournalSection
      icon="⛨" title="Session Contract"
      meta={locked
        ? `Locked${contract.lockedAt ? ` · ${hhmm(contract.lockedAt)}` : ''}`
        : 'Not yet locked'}
    >
      <p className="muted">
        Pre-commit to what you’re aiming to make and what you’ll stop at (accounts
        optional — empty = all accounts). Once locked, the Session view warns at{' '}
        <b>80%</b> and <b>100%</b> of your personal daily stop.
      </p>

      {/* ── Accounts in scope ────────────────────────────────────────────── */}
      <fieldset className="sc-accounts">
        <legend className="muted">
          Accounts in scope — optional, empty = all accounts
        </legend>
        {accountIds.length === 0 ? (
          // ⚠ NOT an empty space. "No accounts" and "all accounts" are opposite
          // meanings for the same empty list, and only one of them is right here.
          <span className="muted">All accounts</span>
        ) : (
          accountIds.map((id) => (
            <span className="chip" key={id}>
              {byId.get(id)?.name ?? '(deleted account)'}
              {locked ? null : (
                <button
                  type="button" className="chip-x"
                  aria-label={`Remove ${byId.get(id)?.name ?? id}`}
                  onClick={() => toggleAccount(id)}
                >×</button>
              )}
            </span>
          ))
        )}
        {locked || !accounts ? null : (
          <>
            <button
              type="button" className="btn"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((o) => !o)}
            >
              {accountIds.length ? '+ account' : '+ select accounts'}
            </button>
            {pickerOpen ? (
              <div className="sc-picker">
                {accounts.length === 0 ? (
                  <p className="muted">No accounts yet — add one in the Accounts tab.</p>
                ) : accounts.map((a) => (
                  <label key={a.id}>
                    <input
                      type="checkbox"
                      checked={accountIds.includes(a.id)}
                      onChange={() => toggleAccount(a.id)}
                    />{' '}
                    {a.name}
                  </label>
                ))}
              </div>
            ) : null}
          </>
        )}
      </fieldset>

      {/* ── The pledge ───────────────────────────────────────────────────── */}
      {/* ⚠ `.field-label`, not a bare span. Both labels rendered at 14px white —
          the largest, brightest text in the card — while every other form label
          in this client is 10px small-caps grey. Nothing was UNSTYLED, so the
          style guard was silent; it was styled as body text because no class
          said otherwise. Caught by opening the tab. */}
      <div className="sc-row2">
        <label className="field">
          <span className="field-label">Target profit $</span>
          <input
            type="number" min="0" step="50" placeholder="aggregate $"
            value={target} disabled={locked || busy}
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Personal DLL $</span>
          <input
            type="number" min="0" step="50" placeholder="aggregate $"
            value={dll} disabled={locked || busy}
            onChange={(e) => setDll(e.target.value)}
          />
        </label>
        <button
          type="button" className={locked ? 'btn' : 'btn primary'}
          disabled={busy || (!locked && !canLock)}
          onClick={() => void commit(!locked)}
        >
          {busy ? 'Saving…' : locked ? 'Unlock' : 'Lock plan'}
        </button>
      </div>

      {/* ⚠ The rule stated where the disabled button is, not in a tooltip. A
          button that will not press and does not say why reads as broken. */}
      {!locked && !canLock ? (
        <p className="muted">Set a target or a personal DLL (or both) to lock.</p>
      ) : null}

      {error ? <p className="red" role="alert">{error}</p> : null}

      {/* ── What the SESSION wrote. Display only, and it is the reason the form
             above touches five fields rather than nine. ──────────────────── */}
      {contract.dllHitAt || contract.targetHitAt || contract.manualPnl != null ? (
        <dl className="kv">
          {contract.dllHitAt ? (
            <>
              <dt className="muted red">Daily stop hit</dt>
              <dd className="red">
                {hhmm(contract.dllHitAt)}
                {contract.dllAcknowledgedAt
                  ? ` · acknowledged ${hhmm(contract.dllAcknowledgedAt)}`
                  : ' · not acknowledged'}
              </dd>
            </>
          ) : null}
          {contract.targetHitAt ? (
            <>
              <dt className="muted green">Target hit</dt>
              <dd className="green">
                {hhmm(contract.targetHitAt)}
                {contract.targetAcknowledgedAt
                  ? ` · acknowledged ${hhmm(contract.targetAcknowledgedAt)}`
                  : ' · not acknowledged'}
              </dd>
            </>
          ) : null}
          {contract.manualPnl != null ? (
            <>
              <dt className="muted">Self-reported P&amp;L</dt>
              <dd>
                {moneyTarget(contract.manualPnl)}{' '}
                <span className="muted">— overrides the computed figure</span>
              </dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {/* ⚠ The acknowledgement is not bookkeeping: every trade timestamped after
          it is flagged as an override of the trader's own cap. */}
      {contract.dllHitAt && contract.dllAcknowledgedAt ? (
        <p className="muted">
          Trades after {hhmm(contract.dllAcknowledgedAt)} are flagged as overrides of
          your own daily stop.
        </p>
      ) : null}

      <p className="muted">
        Acknowledging a breach happens on the Session view, not here — this form only
        edits the pledge.
      </p>
    </JournalSection>
  );
}

/** The five keys the form owns, for updating the cached raw blob after a save. */
function contractOwned(c: SessionContract): Record<string, unknown> {
  return {
    accountIds: [...c.accountIds],
    targetProfit: c.targetProfit,
    personalDLL: c.personalDLL,
    locked: c.locked,
    lockedAt: c.lockedAt,
  };
}

/**
 * HH:MM in the reader's LOCAL zone. ⚠ Keep identical to `hhmmLocal()` in the old
 * client and `hhmm` in Journal.tsx — these are written with `toISOString()` and a
 * raw `.slice(11,16)` showed a 13:56 ET lock as "17:56" in both clients.
 */
const hhmm = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(11, 16);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
